import { useEffect, useState } from "react";
import { getTransparency } from "../../app/api";
import { Link } from "react-router-dom";
import {
  ShoppingBagIcon,
  UsersIcon,
  CreditCardIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from 'react-i18next';
import FloatingShapes from "../common/FloatingShapes";

const HTMLTextView = ({ htmlContent }: any) => {
  if (!htmlContent) return null;
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

export default function Transparency() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [transparencyList, setTransparencyList] = useState<any[]>([]);

  useEffect(() => {
    setLoading(true);
    getTransparency({ level: 1, limit: 50, offset: 0 })
      .then((res: any) => {
        if (res && res.data && res.data.results) {
          setTransparencyList(res.data.results);
        }
      })
      .catch((err) => {
        console.error("Error fetching transparency data:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getIconForTransparency = (name: string) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes("шилэн") || nameLower.includes("төсөв") || nameLower.includes("санхүү")) {
      return CreditCardIcon;
    }
    if (nameLower.includes("худалдан") || nameLower.includes("авах") || nameLower.includes("тендер")) {
      return ShoppingBagIcon;
    }
    if (nameLower.includes("хүний") || nameLower.includes("нөөц") || nameLower.includes("ажилтан")) {
      return UsersIcon;
    }
    return DocumentTextIcon;
  };

  const getHref = (item: any) => {
    if (item.link) {
      // External link
      if (item.link.startsWith("http")) {
        return item.link;
      }
      // Internal route
      return item.link;
    }
    if (item.pdf_url) {
      return item.pdf_url;
    }
    // Default to detail page if content exists
    if (item.content || item._id) {
      return `/transparency/${item._id}`;
    }
    return "#";
  };

  const isExternalLink = (href: string) => {
    return href && (href.startsWith("http") || href.startsWith("//"));
  };

  return (
    <>
      {loading && (
        <div className="w-full h-full fixed top-0 left-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
          </div>
        </div>
      )}

      {/* Dark Hero Header */}
      <div className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="default" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
        <div className="relative pt-40 pb-16 sm:pt-48 sm:pb-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-secondary-300 backdrop-blur-sm">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary-400 animate-pulse" />
              {t('transparency.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight">
              <span className="bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">{t('transparency.title')}</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
              {t('transparency.description')}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="min-h-screen bg-neutral-50 pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            {transparencyList.length > 0 ? (
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                {transparencyList.map((item: any, index: number) => {
                  const Icon = getIconForTransparency(item.name);
                  const href = getHref(item);
                  const external = isExternalLink(href);

                  return (
                    <div
                      key={item._id || item.id}
                      className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 animate-slide-up"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className={`mb-6 flex h-14 w-14 items-center justify-center rounded-2xl ${index % 2 === 0 ? 'bg-primary-600' : 'bg-secondary-600'} shadow-md`}>
                        <Icon className="h-7 w-7 text-white" aria-hidden="true" />
                      </div>
                      <dt className="text-xl font-bold leading-7 text-neutral-900 mb-4">
                        {item.name}
                      </dt>
                      <dd className="mb-6 text-base leading-7 text-neutral-600 min-h-[60px]">
                        {item.content ? (
                          <HTMLTextView htmlContent={item.content.substring(0, 150) + (item.content.length > 150 ? '...' : '')} />
                        ) : (
                          <p>
                            {item.description || t('transparency.detailView')}
                          </p>
                        )}
                      </dd>
                      <div className="mt-6">
                        {external ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl ${index % 2 === 0 ? 'bg-primary-600' : 'bg-secondary-600'} text-white font-semibold text-sm shadow-md hover:shadow-lg hover:scale-105 transform transition-all duration-300`}
                          >
                            {t('transparency.view')} <span>&rarr;</span>
                          </a>
                        ) : (
                          <Link to={href}>
                            <button className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl ${index % 2 === 0 ? 'bg-primary-600' : 'bg-secondary-600'} text-white font-semibold text-sm shadow-md hover:shadow-lg hover:scale-105 transform transition-all duration-300`}>
                              {t('transparency.view')} <span>&rarr;</span>
                            </button>
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              !loading && (
                <div className="text-center bg-white rounded-2xl p-12 shadow-lg">
                  <p className="text-xl text-neutral-600">
                    {t('transparency.noData')}
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
}
